import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, ArrowLeft } from "lucide-react";
import api from "@/lib/api";

export default function SolutionDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [item, setItem] = useState(null);

  useEffect(() => {
    setItem(null);
    api.get(`/solutions/${slug}`).then((r) => setItem(r.data)).catch(() => nav("/solutions"));
  }, [slug, nav]);

  if (!item) return <div className="mia-container py-32 text-[color:var(--mia-muted)]">Loading…</div>;

  return (
    <div>
      {/* Hero */}
      <section className="py-16 lg:py-14 lg:py-16 bg-[color:var(--mia-grey)]">
        <div className="mia-container grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <Link to="/solutions" className="text-[color:var(--mia-navy)] hover:text-[color:var(--mia-gold)] text-sm inline-flex items-center gap-2 mb-4" data-testid="solution-back">
              <ArrowLeft className="w-4 h-4" /> All Services
            </Link>
            <h1 className="font-display font-black text-4xl sm:text-5xl text-[color:var(--mia-navy)] tracking-tight leading-tight uppercase">
              {item.title}
            </h1>
            <p className="text-[color:var(--mia-navy)] font-display font-bold text-lg sm:text-xl mt-4 leading-snug" data-testid="solution-tagline">
              {item.tagline}
            </p>
            <div className="w-16 h-1 bg-[color:var(--mia-green)] my-6" />
            <div className="text-[color:var(--mia-text)] leading-relaxed space-y-4" data-testid="solution-description">
              {(item.description || "").split("\n\n").map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
            <Link to={`/contact?solution=${item.slug}`} className="mia-btn mia-btn-primary mt-8" data-testid="solution-cta-quote">
              Request A Consultation <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="rounded-md overflow-hidden shadow-2xl">
            <img src={item.hero_image || item.image} alt={item.title} className="w-full h-auto block" />
          </div>
        </div>
      </section>

      {/* Applications & Benefits */}
      <section className="py-14 lg:py-16 bg-white">
        <div className="mia-container grid lg:grid-cols-2 gap-14">
          {item.applications?.length > 0 && (
            <div>
              <h3 className="font-display font-bold text-[color:var(--mia-navy)] text-lg tracking-wide uppercase">Applications</h3>
              <div className="w-10 h-[3px] bg-[color:var(--mia-gold)] mt-3 mb-6" />
              <ul className="space-y-3">
                {item.applications.map((a) => (
                  <li key={a} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[color:var(--mia-gold)] shrink-0 mt-0.5" />
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {item.benefits?.length > 0 && (
            <div>
              <h3 className="font-display font-bold text-[color:var(--mia-navy)] text-lg tracking-wide uppercase">Benefits</h3>
              <div className="w-10 h-[3px] bg-[color:var(--mia-gold)] mt-3 mb-6" />
              <ul className="space-y-3">
                {item.benefits.map((b) => (
                  <li key={b} className="flex items-start gap-3">
                    <ArrowRight className="w-4 h-4 text-[color:var(--mia-navy)] shrink-0 mt-1" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {item.specifications?.length > 0 && (
          <div className="mia-container mt-16">
            <h3 className="font-display font-bold text-[color:var(--mia-navy)] text-lg tracking-wide uppercase">Specifications</h3>
            <div className="w-10 h-[3px] bg-[color:var(--mia-gold)] mt-3 mb-6" />
            <ul className="grid md:grid-cols-2 gap-3">
              {item.specifications.map((s, i) => (
                <li key={i} className="bg-[color:var(--mia-grey)] rounded-md px-5 py-4 text-sm">
                  {s}
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* Bottom CTA */}
      <section className="py-14 lg:py-16 bg-[color:var(--mia-navy)] text-white">
        <div className="mia-container grid lg:grid-cols-[1.4fr,1fr] gap-8 items-center">
          <div>
            <div className="text-[0.72rem] font-bold tracking-[0.24em] uppercase text-[color:var(--mia-green)]">Ready to build?</div>
            <h2 className="font-display font-black text-2xl sm:text-3xl lg:text-4xl mt-3 leading-tight tracking-tight uppercase">
              {item.cta_text || "Request Consultation"}
            </h2>
            <p className="text-white/75 mt-4 max-w-xl">
              Speak with our marine engineering team. We'll assess your requirements and shape a solution around your project, budget and timeline.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row lg:flex-col gap-3">
            <Link to="/request-consultation" className="mia-btn mia-btn-primary justify-center" data-testid="solution-cta-consultation">
              Request Consultation <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" className="mia-btn mia-btn-outline-white justify-center" data-testid="solution-cta-contact">
              Get A Quote
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
